/**
 * TNK-130 phase D — wizard step 1: pick the CSV, parse it client-side,
 * post the parsed payload to /api/imports, navigate to /tools/imports/$id
 * for progress + review.
 *
 * Parsing stays in the browser (the existing csvParser.ts works without
 * modification) because it's CPU-only with no network — sub-second on
 * realistic imports — and porting it server-side would be its own
 * project. The slow parts (image fetch, set-code lookup) run in the
 * server-side enrichment worker added in phase C.
 */
import { useRef, useState } from "react";
import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { AlertCircle, FileText, Upload } from "lucide-react";

import { BackButton } from "@/components/BackButton";
import { FIGMA } from "@/components/import/figmaStyles";
import { ImportStepper } from "@/components/import/ImportStepper";
import { PrimaryButton } from "@/components/PrimaryButton";
import {
  mapEbayFileExchangeRow,
  parseCardmarketRow,
  parseCsv,
  parsePriceChartingRow,
  type PcCard,
} from "@/features/csvParser";
import { createImportDraft, type NewDraftItem } from "@/features/importsApi";

export const Route = createFileRoute("/tools/imports/new")({
  component: NewImportWizard,
});

type DetectedFormat =
  | "none"
  | "pricecharting"
  | "ebay-file-exchange"
  | "cardmarket"
  | "generic";

interface PreviewState {
  format: DetectedFormat;
  totalRows: number;
  perGame?: Record<string, number>;
  items: NewDraftItem[];
}

const FORMAT_LABEL: Record<DetectedFormat, string> = {
  none: "Unknown",
  pricecharting: "PriceCharting CSV",
  "ebay-file-exchange": "eBay File Exchange CSV",
  cardmarket: "Cardmarket CSV",
  generic: "Generic CSV",
};

function NewImportWizard() {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [csvText, setCsvText] = useState("");
  const [filename, setFilename] = useState<string>("");
  const [preview, setPreview] = useState<PreviewState | null>(null);
  const [error, setError] = useState<string>("");
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file: File) => {
    setError("");
    setPreview(null);
    setFilename(file.name);
    const text = await file.text();
    setCsvText(text);
    parseAndPreview(text);
  };

  const parseAndPreview = (text: string) => {
    setError("");
    setPreview(null);
    if (!text.trim()) {
      setError("CSV is empty.");
      return;
    }

    const { rows, isPriceCharting, isEbayFileExchange, isCardmarket } =
      parseCsv(text);
    if (rows.length === 0) {
      setError("Could not parse CSV — check the header row.");
      return;
    }

    let format: DetectedFormat;
    let parsedRows: PcCard[];
    if (isPriceCharting) {
      format = "pricecharting";
      parsedRows = rows.map(parsePriceChartingRow);
    } else if (isEbayFileExchange) {
      format = "ebay-file-exchange";
      parsedRows = rows.map(mapEbayFileExchangeRow);
    } else if (isCardmarket) {
      format = "cardmarket";
      parsedRows = rows.map(parseCardmarketRow);
    } else {
      format = "generic";
      // Generic CSV: copy a minimal subset into the parsed payload. The
      // server's promote helper falls back to safe defaults for anything
      // missing, and the operator can fix any field in the review step.
      parsedRows = rows.map((row) => ({
        name: row.name ?? "",
        variant: row.variant ?? "",
        cardNumber: row.cardnumber ?? row.number ?? "",
        setName: row.setname ?? row["set name"] ?? "",
        marketPrice:
          parseFloat(row.marketprice ?? row["market price"] ?? "0") || 0,
        costBasis: parseFloat(row.costbasis ?? row["cost basis"] ?? "0") || 0,
        condition: "NM",
        quantity: parseInt(row.quantity ?? "1") || 1,
        notes: row.notes ?? "",
        folder: row.storagelocation ?? row.location ?? row.folder ?? "",
        game: "Pokemon",
      }));
    }

    const items: NewDraftItem[] = parsedRows.map((p, i) => ({
      csvRow: rows[i] ?? {},
      parsed: p as unknown as Record<string, unknown>,
    }));

    const perGame: Record<string, number> | undefined =
      format === "ebay-file-exchange"
        ? parsedRows.reduce<Record<string, number>>((acc, r) => {
            const g = r.game ?? "Pokemon";
            acc[g] = (acc[g] ?? 0) + 1;
            return acc;
          }, {})
        : undefined;

    setPreview({
      format,
      totalRows: parsedRows.length,
      perGame,
      items,
    });
  };

  const handleUpload = async () => {
    if (!preview) return;
    setUploading(true);
    setError("");
    try {
      const { draft } = await createImportDraft({
        sourceFormat: preview.format,
        sourceFilename: filename || undefined,
        items: preview.items,
      });
      void navigate({
        to: "/tools/imports/$id",
        params: { id: draft.id },
      });
    } catch (err) {
      setError((err as Error).message ?? "Upload failed.");
      setUploading(false);
    }
  };

  return (
    // Section shell, full-width body — matches /tools/storage-bins etc.
    <section className="flex h-full flex-col space-y-6">
      <BackButton fallback="/tools" />
      <ImportStepper currentStep="configure" />
      <div className="space-y-1">
        <h1
          className="text-2xl"
          style={{
            fontFamily: FIGMA.fontHeading,
            fontWeight: 700,
            color: FIGMA.fg,
            letterSpacing: "0.02em",
          }}
        >
          Import cards
        </h1>
        <p className="text-sm" style={{ color: FIGMA.muted }}>
          Pick a CSV — we'll parse it, attach photos in the background, and give
          you a chance to review every row before anything goes live.
        </p>
      </div>

      <div className="space-y-4 rounded-lg border border-border bg-card p-6">
        <div>
          <h2 className="text-sm font-semibold text-foreground">
            1. Pick the file
          </h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Supported formats are auto-detected: eBay File Exchange (mixed Magic
            + Pokémon routes per row via *C:Game), PriceCharting, Cardmarket, or
            a generic <code>name,setName,cardNumber,...</code> CSV.
          </p>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv,text/plain"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void handleFile(f);
            }}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="flex flex-1 items-center justify-center gap-2 rounded-md border border-dashed px-4 py-3 text-sm font-medium transition-colors"
            style={{
              borderColor: FIGMA.indigoBorder,
              background: FIGMA.indigoBg,
              color: FIGMA.indigoLight,
              fontFamily: FIGMA.fontBody,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = FIGMA.indigoBgHover;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = FIGMA.indigoBg;
            }}
          >
            <Upload size={16} />
            {filename ? `Replace (${filename})` : "Choose CSV file"}
          </button>
        </div>

        <details className="rounded-md border border-border bg-muted/20 p-3">
          <summary className="cursor-pointer text-xs font-medium text-muted-foreground">
            …or paste CSV text instead
          </summary>
          <textarea
            value={csvText}
            onChange={(e) => {
              setCsvText(e.target.value);
              setFilename("");
              if (e.target.value.trim()) parseAndPreview(e.target.value);
              else setPreview(null);
            }}
            placeholder="Paste CSV text here…"
            rows={8}
            className="mt-2 w-full rounded border border-border bg-background p-2 font-mono text-xs"
          />
        </details>

        {error && (
          <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}
      </div>

      {preview && (
        <div className="space-y-4 rounded-lg border border-border bg-card p-6">
          <div className="flex items-start gap-3">
            <FileText size={20} className="mt-0.5 text-primary" />
            <div className="flex-1">
              <h2 className="text-sm font-semibold text-foreground">
                2. Review the detection
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                <span className="font-medium text-foreground">
                  {FORMAT_LABEL[preview.format]}
                </span>{" "}
                detected · {preview.totalRows} row
                {preview.totalRows === 1 ? "" : "s"}
                {preview.perGame && Object.keys(preview.perGame).length > 0 && (
                  <>
                    {" "}
                    (
                    {Object.entries(preview.perGame)
                      .sort((a, b) => b[1] - a[1])
                      .map(([g, n]) => `${n} ${g}`)
                      .join(" · ")}
                    )
                  </>
                )}
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between border-t border-border pt-4">
            <p className="text-xs text-muted-foreground">
              Nothing lands in inventory until you confirm at the end of review.
            </p>
            <PrimaryButton
              onClick={handleUpload}
              disabled={uploading || preview.totalRows === 0}
              loading={uploading}
              leadingIcon={<Upload size={16} />}
              label={
                uploading
                  ? "Uploading…"
                  : `Start import · ${preview.totalRows} row${preview.totalRows === 1 ? "" : "s"}`
              }
            />
          </div>
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        Looking for a previous draft?{" "}
        <Link to="/tools/imports" className="underline hover:text-foreground">
          See all imports
        </Link>
        .
      </p>
    </section>
  );
}
